import { replaceHydratedHtml } from '../shared/replace-hydrated-html';

// The markup each pane last painted, with the first node it produced. A caster_update that builds the same markup keeps the pane
// (rebuild audit 2026-10-25): every walk, hit and turn repainted every mounted pane of the caster and re-hydrated its icons and
// selects. Any other write into the pane replaces that first node, so the next paint goes through.
const paintedPanes = new WeakMap<HTMLElement, { html: string; first: ChildNode | null }>();
/** True when the pane still shows exactly the `html` this guard last painted into it. */
export function paintedMarkupHolds(container: HTMLElement, html: string): boolean {
    const held = paintedPanes.get(container);
    return !!held && held.html === html && held.first !== null && held.first === container.firstChild;
}
/** Writes `html` unless the pane already shows exactly it; true when it wrote (callers re-hydrate only then). */
export function paintMarkup(container: HTMLElement, html: string): boolean {
    if (paintedMarkupHolds(container, html)) return false;
    replaceHydratedHtml(container, html);
    paintedPanes.set(container, { html, first: container.firstChild });
    return true;
}

/** Reconcile keyed pane markup against its prior source, retaining unchanged hydrated rows. */
export function paintInventoryMarkup(container: HTMLElement, html: string, rowKey?: (node: Node) => string): boolean {
    if (paintedMarkupHolds(container, html)) return false;
    const held = paintedPanes.get(container);
    if (!held || held.first !== container.firstChild) return paintMarkup(container, html);
    const before = document.createElement('template'), next = document.createElement('template');
    before.innerHTML = held.html; next.innerHTML = html;
    const key = rowKey ?? ((node: Node): string => node instanceof Element
        ? node.id || node.getAttribute('data-inventory_id') || node.getAttribute('data-ft-inventory-id') || '' : '');
    const retire = (node: ChildNode): void => {
        if (node instanceof HTMLElement) {
            const page = window as Window & { destroyTomSelectIn?: (root: Element) => void; disposeBootstrapTooltipsWithin?: (root: Element) => void };
            page.destroyTomSelectIn?.(node); page.disposeBootstrapTooltipsWithin?.(node);
        }
        node.remove();
    };
    const sync = (parent: Node, old: Node, desired: Node): void => {
        const previous = [...old.childNodes], children = [...parent.childNodes], used = new Set<ChildNode>();
        // Tom Select inserts a sibling absent from the source markup. Keep it with its select,
        // including when positional siblings follow it in a changed row.
        const owned = new Map<ChildNode, HTMLElement>();
        for (const node of children) if (node instanceof HTMLSelectElement) {
            const wrapper = (node as HTMLSelectElement & { tomselect?: { wrapper: HTMLElement } }).tomselect?.wrapper;
            if (wrapper?.parentNode === parent) owned.set(node, wrapper);
        }
        const wrappers = new Set<ChildNode>(owned.values());
        const live = children.filter(node => !wrappers.has(node));
        let cursor = parent.firstChild;
        for (const [index, target] of [...desired.childNodes].entries()) {
            const identity = key(target), priorIndex = identity ? previous.findIndex(node => key(node) === identity) : index;
            const prior = previous[priorIndex];
            let node = identity ? live.find(node => key(node) === identity) : live[priorIndex];
            if (!prior || !node || used.has(node) || prior.nodeType !== target.nodeType || prior.nodeName !== target.nodeName) {
                node = target.cloneNode(true) as ChildNode;
            } else if (!prior.isEqualNode(target)) {
                if (node instanceof Element && prior instanceof Element && target instanceof Element) {
                    for (const attr of [...prior.attributes]) if (!target.hasAttribute(attr.name)) node.removeAttribute(attr.name);
                    for (const attr of [...target.attributes]) if (prior.getAttribute(attr.name) !== attr.value) node.setAttribute(attr.name, attr.value);
                    if (node instanceof HTMLInputElement && target instanceof HTMLInputElement && prior instanceof HTMLInputElement) {
                        if (prior.value !== target.value) node.value = target.value;
                        if (prior.checked !== target.checked) node.checked = target.checked;
                    }
                    sync(node, prior, target);
                } else if (target.nodeType === Node.TEXT_NODE) node.nodeValue = target.nodeValue;
            }
            if (node !== cursor) parent.insertBefore(node, cursor);
            used.add(node);
            const wrapper = owned.get(node);
            if (wrapper) {
                if (node.nextSibling !== wrapper) parent.insertBefore(wrapper, node.nextSibling);
                used.add(wrapper);
            }
            cursor = (wrapper ?? node).nextSibling;
        }
        for (const node of children) if (!used.has(node)) retire(node);
    };
    sync(container, before.content, next.content);
    paintedPanes.set(container, { html, first: container.firstChild });
    return true;
}

/** A spell's source distinguishes equipment grants of the same spell. */
export function paintCastableMarkup(container: HTMLElement, html: string): boolean {
    return paintInventoryMarkup(container, html, node => {
        if (!(node instanceof Element)) return '';
        const spell = node.getAttribute('data-ft-spell-id');
        return spell ? `${spell}|${node.getAttribute('data-ft-spell-source') ?? ''}`
            : node.id || node.getAttribute('data-ft-spell-section-key') || '';
    });
}
